<template>
   <button type="button" :disabled="isDisabled" :class="classFromProps" @click="$emit('click')">
      <slot v-if="!text"></slot>
      {{ text }}
   </button>
</template>

<script setup lang="ts">
import { getButtonClass } from "./button.composable";

import { Theme } from "@/types";
import { type ButtonStyling } from "./button-types";

interface DefaultButtonCTAData {
   type?: "button";
   hideOn?: "logged_in" | "logged_out";
   text?: string; // can be passed in via slot
   action?: string; // action is not used in this component (its only passed here to help with bindings)
}

interface DefaultButtonProp extends ButtonStyling, DefaultButtonCTAData {
   isIcon?: boolean;
}

export type { DefaultButtonCTAData, DefaultButtonProp };

const props = withDefaults(defineProps<DefaultButtonProp>(), {
   size: "md",
   roundedEdges: "full",
   isDisabled: false,
   color: Theme.BrandDark,
   styleType: "filled",
   isIcon: false,
});

// computed
// -----------------------------------------
const classFromProps = computed(() => {
   if (!props) return null;

   if (props.isIcon) return ["flex", "items-center", "justify-center", "icon-button", ...getButtonClass(props)];

   const displayClass = props.styleType.toLowerCase() !== "text-only" ? "block" : "";
   return [displayClass, ...getButtonClass(props)];
});

defineEmits(["click"]);
</script>

<style scoped lang="scss">
@use "./button.scss";

.icon-button.size {
   &-sm {
      @apply p-2;
      min-width: 2rem;
   }
   &-md {
      @apply p-3;
      min-width: 3rem;
   }
   &-lg {
      @apply p-4;
      min-width: 5rem;
   }
}
</style>
